import { useSelector, useDispatch } from 'react-redux'
import { useState } from 'react'
import Select from 'react-select'
import { WhiteLabelCheck, selectStyles } from 'apps/builder/components/shared'
import {
  referenceDataFileListSelector,
  referenceDataEntries,
  doesFieldContainRefDataSelector,
  activeFieldInTableSelector,
  normalScreenRefDataSelector,
  tableScreenRefDataSelector,
  entriesSelector,
  whiteLabelSelector,
  activeAdvancedLoopSheetSelector,
  advancedLoopScreenRefDataSelector,
} from 'apps/builder/store/selectors'

import {
  createReferenceDataEntry,
  updateReferenceDataEntry,
  deleteReferenceDataEntry,
} from 'apps/builder/store/actions/referenceData'

// TODO: Delete file/directory when new Builder 3 UI is released

const ReferenceDataInfoBlock = () => {
  const isWhiteLabeled = useSelector(whiteLabelSelector)

  return (
    <div className="info--block--container">
      <div className="foundry--surface surface--info info--block">
        <p>
          You can auto-populate any {isWhiteLabeled ? '' : 'GoCanvas'} form with
          data from a reference file. For example, you could select one of your
          customer names from a drop-down and subsequent fields (such as phone
          number or email) would be automatically filled in based on data
          provided in your reference file.{' '}
          <span>
            {' '}
            <WhiteLabelCheck>
              <a
                className="info--block--link"
                href="https://help.gocanvas.com/hc/en-us/sections/26599885125527-Reference-Data"
                target="blank"
              >
                Learn More
              </a>
            </WhiteLabelCheck>
          </span>
        </p>
      </div>
    </div>
  )
}

export const ReferenceData = ({
  guid, label, placeholder, keyField,
  dependent_entry, reference_data_column, reference_data_id,
}) => {
  const isAdvancedLoop = useSelector(activeAdvancedLoopSheetSelector)
  const isTableOrSimpleLoop = useSelector(activeFieldInTableSelector)
  const refData = useSelector(referenceDataFileListSelector)
  const refDataEntries = useSelector(referenceDataEntries)
  const entries = useSelector(entriesSelector)
  const doesFieldContainRefData = useSelector(doesFieldContainRefDataSelector)
  const dispatch = useDispatch()

  let dependentEntriesAndScreens
  if (isAdvancedLoop) {
    dependentEntriesAndScreens = useSelector(advancedLoopScreenRefDataSelector)
  } else if (isTableOrSimpleLoop) {
    dependentEntriesAndScreens = useSelector(tableScreenRefDataSelector)
  } else {
    dependentEntriesAndScreens = useSelector(normalScreenRefDataSelector)
  }

  const sortByLabel = (a, b) => a.label.localeCompare(b.label)

  const entryRefData = refDataEntries.find(
    ({ guid: ref_guid }) => ref_guid == guid
  )

  const refFile = refData.find(({ id }) => id == reference_data_id)

  const findSelectedRefData = () => {
    if (doesFieldContainRefData) {
      const sheetEntries = dependentEntriesAndScreens.find(({ entries }) => {
        if (dependent_entry && dependent_entry.guid) {
          return entries.find(e => e.guid == dependent_entry.guid)
        }
      })

      const { sheet = {}, primarySheet } = sheetEntries || {}
      const entry = entries.find(e => dependent_entry?.guid == e.guid)
      const tableLabel =
        keyField && primarySheet
          ? sheet.description || sheet.placeholder || ''
          : primarySheet?.description || primarySheet?.placeholder || ''

      return dependent_entry && dependent_entry.guid && entry
        ? [
            { label: refFile.name, value: reference_data_id },
            {
              label: reference_data_column,
              value: reference_data_column,
            },
            {
              label: primarySheet
                ? tableLabel
                : sheet.description || sheet.placeholder,
              value: primarySheet ? primarySheet.id : sheet.id,
            },
            {
              label: entry.label || entry.placeholder,
              value: entry.guid,
            },
          ]
        : [
            refFile && {
              label: refFile.name,
              value: reference_data_id,
            },
            {
              label: reference_data_column,
              value: reference_data_column,
            },
          ]
    } else {
      return null
    }
  }

  const getSheets = () => {
    return dependentEntriesAndScreens
      .map(({ sheet, primarySheet = {} }) => {
        const tableLabel =
          keyField && primarySheet
            ? sheet.description || primarySheet.placeholder || ''
            : primarySheet.description || primarySheet.placeholder || ''

        return {
          label: primarySheet.hasOwnProperty('id')
            ? tableLabel
            : sheet.description || sheet.placeholder,
          value: primarySheet.hasOwnProperty('id') ? primarySheet.id : sheet.id,
        }
      })
      .filter(a => a)
  }

  const filterEntriesBySelectedFile = (entries = []) => {
    return entries
      .map(e => {
        const foundDependentEntry = refDataEntries.find(re => re.guid == e.guid)

        if (
          e.guid !== guid &&
          columnCheck(foundDependentEntry) &&
          foundDependentEntry &&
          (foundDependentEntry.dependent_entry == null ||
            foundDependentEntry.dependent_entry.guid != guid)
        ) {
          return { label: e.label, value: e.guid }
        }
      })
      .filter(a => a)
      .sort(sortByLabel)
  }

  const getValidRefDependentEntries = () => {
    const foundSheet =
      dependentEntriesAndScreens.find(
        ({ sheet, primarySheet, isSubSheet }) =>
          sheet.id == selectedSheet.value ||
          (isSubSheet && primarySheet.id == selectedSheet.value)
      ) || {}

    const { entries = [] } = foundSheet

    return filterEntriesBySelectedFile(entries)
  }

  const createOrUpdate = doesFieldContainRefData
    ? updateReferenceDataEntry
    : createReferenceDataEntry

  const selectedOption = findSelectedRefData()

  const [selectedFile, setSelectedFile] = useState(
    doesFieldContainRefData ? selectedOption[0] : null
  )

  const [selectedSheet, setSelectedSheet] = useState(
    doesFieldContainRefData && dependent_entry ? selectedOption[2] : null
  )

  const setSheet = val => {
    if (val.value == 'None') {
      dispatch(
        createOrUpdate({
          guid: guid,
          dependent_entry: null,
        })
      )
    }
    setSelectedSheet(val)
  }

  const updateDependentEntry = entry => {
    if (entry && entry.value == 'None') {
      dispatch(
        createOrUpdate({
          guid: guid,
          dependent_entry: null,
        })
      )
    } else {
      dispatch(
        createOrUpdate({
          guid: guid,
          dependent_entry: entry ? { guid: entry.value } : null,
        })
      )
    }
  }

  const onRefFileSelect = refid => {
    if (refid.value == 'None') {
      dispatch(deleteReferenceDataEntry({ guid: guid }))
    } else {
      dispatch(
        createOrUpdate({
          guid: guid,
          reference_data_column: null,
          reference_data_id: refid.value,
          label: label || placeholder,
          dependent_entry: null,
        })
      )
    }

    setSelectedFile(refid)
    setSelectedSheet(null)
  }

  const getAssociatedColumns = (columns = []) =>
    columns
      .map(columnData => {
        return {
          label: columnData,
          value: columnData,
        }
      })
      .sort(sortByLabel)

  const { value = '' } = selectedFile || {}
  const data = refData.find(({ id }) => id == value) || {}
  const { headers: reference_columns = [] } = data
  const columnOptions = getAssociatedColumns(reference_columns)

  /**
   *  Checks the columns with the selected file & valid dependent entries to see if there are like
   * columns to show it as a valid dependent_entry
   */
  const columnCheck = ({ reference_data_id }) => {
    /** Pull the ref file of the possible dependent entry */
    const ref_file_column_lookup = refData.find(
      ref => ref.id == reference_data_id
    )

    /** Pull columns off the ref file.  */
    const { headers: reference_columns = [] } = ref_file_column_lookup || {}

    /** Make sure selected option is defined */
    if (selectedOption && selectedOption[1]) {
      // Look at the ref column array to see if the current/active field's columns match possible dependent entry columns
      return columnOptions.some(({ value }) => {
        return reference_columns.includes(value)
      })
    }
  }

  const sharedSelectProps = {
    styles: selectStyles,
    className: 'input--placeholder',
    classNamePrefix: 'react-select',
  }

  const ref_files = [
    { value: 'None', label: 'None' },
    ...refData.map(({ id, name }) => {
      return {
        label: name,
        value: id,
      }
    }),
  ]

  const update = value => {
    dispatch(
      createOrUpdate({
        guid: guid,
        reference_data_column: value.label,
        reference_data_id: selectedFile.value,
        label: label || placeholder,
      })
    )
  }

  return (
    <div className="settings--field">
      <div className="settings--row no-border no-top">
        <div className="reference-data-dropdowns--container">
          <div className="label-floating">
            <Select
              {...sharedSelectProps}
              onChange={data => {
                onRefFileSelect(data)
              }}
              isDisabled={false}
              options={ref_files}
              value={selectedFile}
            />
            <label className="label-default-value">Reference Data File</label>
          </div>

          <div className="label-floating">
            <Select
              {...sharedSelectProps}
              onChange={value => {
                update(value)
              }}
              options={columnOptions}
              value={
                refFile && selectedOption && selectedOption[1].label
                  ? selectedOption[1]
                  : null
              }
              isDisabled={columnOptions.length == 0}
            />
            <label className="label-default-value">Reference Column</label>
          </div>

          <div className="label-floating">
            <Select
              {...sharedSelectProps}
              onChange={val => {
                setSheet(val)
                updateDependentEntry(null)
              }}
              options={[{ label: 'None', value: 'None' }, ...getSheets()]}
              isDisabled={
                !reference_data_column || reference_data_column == 'None'
              }
              value={selectedSheet}
            />
            <label className="label-default-value">Reference Screen</label>
          </div>

          <div className="label-floating">
            <Select
              {...sharedSelectProps}
              onChange={value => {
                updateDependentEntry(value)
              }}
              options={
                selectedSheet
                  ? [
                      { value: 'None', label: 'None' },
                      ...getValidRefDependentEntries(),
                    ]
                  : []
              }
              value={
                doesFieldContainRefData && dependent_entry
                  ? selectedOption[3]
                  : null
              }
              isDisabled={!selectedSheet}
            />
            <label className="label-default-value">Reference Field</label>
          </div>
        </div>
        <ReferenceDataInfoBlock />
      </div>
    </div>
  )
}
